import React, { useState } from 'react';
import { Drawer, Input, Popover, Spin } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import { MONO, PRIMARY, SHELL_THEME as THEME, shellVars } from '../../shellTokens';
import './stockChrome.css';

/**
 * Chrome shared by the stock screens — Fabric Store and Fabric Sales.
 *
 * These pieces were duplicated verbatim across both files, which is how the two
 * screens started drifting apart in the details (a tile's padding here, a
 * drawer's header there). They are presentational only: the data and the
 * decisions stay on the screen that owns them.
 */

/* ---- Drawer ------------------------------------------------------------ */

/**
 * A Drawer wearing the shell's palette and type scale.
 *
 * antd's stock header is a 16px title and a bare ✕; the redesign's is a
 * 14.5px/700 title over a dim subtitle, with the close control as a rounded
 * chip. Header and footer are antd's own slots rather than markup inside the
 * body, so both stay pinned while a long form scrolls between them.
 *
 * The panel portals to <body>, outside the element the screen declares its CSS
 * variables on, so the backgrounds are passed as plain hex and the variables
 * are re-declared on the content wrapper for everything nested inside.
 */
export function PanelDrawer({
  open,
  onClose,
  title,
  subtitle,
  width,
  darkMode,
  footer,
  bodyPadding = 24,
  children,
}) {
  const t = THEME[darkMode ? 'dark' : 'light'];
  const rule = `1px solid ${t.border}`;
  // Header, body and footer are siblings inside the portalled panel, so the
  // variables have to be declared on each in turn — one wrapper around the body
  // would leave the footer's buttons reading colours that resolve to nothing.
  //
  // `color` and `fontFamily` are set here too, and deliberately. antd puts
  // colorText on .ant-drawer-title and nowhere else, index.css leaves `body`
  // without a colour, and the dark palette lives on .et-shell — which a
  // portalled panel is not inside. Anything in here that does not name its own
  // colour would otherwise inherit the UA default and go near-black on a dark
  // surface. This is the shell root's own declaration, restated where the
  // portal broke the inheritance chain.
  const vars = {
    ...shellVars(darkMode),
    color: 'var(--text)',
    fontFamily: 'Outfit, system-ui, sans-serif',
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      closable={false}
      drawerStyle={{ background: t.surface }}
      headerStyle={{ background: t.surface, borderBottom: rule, padding: '14px 18px' }}
      bodyStyle={{ background: t.surface, padding: bodyPadding }}
      footerStyle={{ background: t.surface, borderTop: rule, padding: '12px 18px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: '700 14.5px Outfit, sans-serif', color: t.text }}>{title}</div>
            {subtitle && (
              <div style={{ font: '400 11.5px Outfit, sans-serif', color: t.dim, marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(); }}
            style={{
              flex: 'none', width: 26, height: 26, borderRadius: 7,
              background: t.surface2, color: t.muted, fontSize: 11,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <CloseOutlined />
          </span>
        </div>
      }
      footer={footer ? <div style={vars}>{footer}</div> : undefined}
    >
      <div style={vars}>{children}</div>
    </Drawer>
  );
}

/* ---- Tiles and stats --------------------------------------------------- */

export function StatTile({ icon, tint, label, value, foot }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 19, height: 19, borderRadius: 6, background: `${tint}29`, color: tint, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ font: '700 18px monospace', color: 'var(--text)' }}>{value}</div>
      {foot && <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>{foot}</div>}
    </div>
  );
}

export function SummaryStat({ label, value, tint }) {
  return (
    <div>
      <div style={miniLabel}>{label}</div>
      <div style={{ font: '700 18px monospace', color: tint, marginTop: 2 }}>{value}</div>
    </div>
  );
}

/* ---- Toolbar ----------------------------------------------------------- */

/**
 * A Popover's menu. Popovers portal to <body>, so the colours are hex rather
 * than var(--x), which does not resolve out there.
 */
export function MenuList({ items, active, onSelect, darkMode, searchable, searchPlaceholder }) {
  const t = THEME[darkMode ? 'dark' : 'light'];
  const [term, setTerm] = useState('');

  // A search box past a certain length, because a list you have to scroll to
  // read is a list you cannot answer a question with. Below that it is pure
  // clutter — five fabrics fit on screen at once.
  const withSearch = searchable ?? items.length > 8;
  const shown = term.trim()
    ? items.filter((it) => String(it.label ?? '').toLowerCase().includes(term.trim().toLowerCase()))
    : items;

  return (
    <div style={{ minWidth: 190, maxHeight: 340, overflowY: 'auto', background: t.surface2, borderRadius: 8 }}>
      {withSearch && (
        <div style={{ padding: '6px 6px 0' }}>
          <Input
            autoFocus
            size="small"
            allowClear
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={searchPlaceholder || 'Search'}
            prefix={<SearchOutlined style={{ color: t.dim, fontSize: 11 }} />}
            // The popover closes on Enter otherwise, before the list narrows.
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div style={{ padding: 6 }}>
        {withSearch && shown.length === 0 && (
          <div style={{ padding: '10px 10px 12px', font: '400 12px Outfit, sans-serif', color: t.dim }}>
            Nothing matches “{term.trim()}”.
          </div>
        )}
        {shown.map((it) => {
          const isActive = active !== undefined && active === it.key;
          return (
            <div
              key={String(it.key)}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(it.key)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSelect(it.key); }}
              style={{ height: 34, borderRadius: 8, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', background: isActive ? t.hover : 'transparent' }}
            >
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: t.text }}>{it.label}</span>
              {isActive && <CheckOutlined style={{ marginLeft: 'auto', color: PRIMARY, fontSize: 11 }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FilterChip({ label, value, onClear }) {
  return (
    <span style={{
      height: 26, padding: '0 5px 0 9px', borderRadius: 7, background: 'var(--surface2)',
      border: '1px solid var(--border)', color: 'var(--text)',
      font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <span style={{ opacity: .75, fontWeight: 500, color: 'var(--muted)' }}>{label}</span>
      {value}
      <span
        role="button"
        tabIndex={0}
        onClick={onClear}
        onKeyDown={(e) => { if (e.key === 'Enter') onClear(); }}
        style={{ width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', background: 'var(--surface)' }}
      >
        <CloseOutlined />
      </span>
    </span>
  );
}

/* ---- Footer ------------------------------------------------------------ */

/**
 * How many rows a page holds, as the rest of the app asks it: the word "Rows",
 * a 28px chip carrying the number, and the same MenuList popover every other
 * filter on these screens opens.
 *
 * The fabric screens used an antd Select reading "15 / page" instead. It sat at
 * antd's own control height — 8px taller than the pager beside it and taller
 * than every filter button above it — and it was the only select of its kind in
 * the app, so the footer had one control that had plainly wandered in from
 * somewhere else. Orders, Customers, Users and Ready Stock have all used the
 * chip since the redesign.
 *
 * Open state is the component's own: unlike the toolbar popovers there is
 * nothing here to coordinate with, and threading a menu key through four
 * screens to get that would buy nothing.
 */
export function RowsPicker({ value, options, onChange, darkMode }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      content={(
        <MenuList
          darkMode={darkMode}
          items={options.map((n) => ({ key: n, label: String(n) }))}
          active={value}
          onSelect={(v) => { setOpen(false); onChange(v); }}
        />
      )}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
        <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Rows</span>
        <span style={{ height: 28, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: `600 12px ${MONO}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          {value}
          <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
        </span>
      </span>
    </Popover>
  );
}

/**
 * The pager the rest of the app uses: numbered pages with the current one
 * filled, first and last always reachable, an ellipsis for the gap, and a jump
 * box once there are more than ten.
 *
 * The fabric screens had `‹ 4 / 37 ›` instead — two arrows and a fraction. It
 * is smaller code and a worse control: page 30 of 37 is nineteen clicks away,
 * and it was the only pager in the app shaped that way, so a screen that
 * otherwise matches Orders and Customers stopped matching them at the bottom of
 * the table.
 *
 * `onGo` is handed the page number and nothing else; the screens each pair
 * their own setPage with their own fetch.
 */
export function Pagination({ page, totalPages, onGo }) {
  const [jump, setJump] = useState('');
  // First, last, and the current page with a neighbour either side. Everything
  // between collapses into the ellipsis below.
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <PageBtn disabled={page <= 1} onClick={() => onGo(page - 1)} label="Previous page">‹</PageBtn>
      {pages.map((p, i, arr) => (
        <React.Fragment key={p}>
          {i > 0 && arr[i - 1] !== p - 1 && (
            <span style={{ height: 30, minWidth: 22, color: 'var(--dim)', font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>…</span>
          )}
          <PageBtn active={p === page} onClick={() => onGo(p)} label={`Page ${p}`}>{p}</PageBtn>
        </React.Fragment>
      ))}
      <PageBtn disabled={page >= totalPages} onClick={() => onGo(page + 1)} label="Next page">›</PageBtn>
      {totalPages > 10 && (
        <>
          <span style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 5px' }} />
          <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Go to</span>
          <Input
            size="small"
            placeholder="page"
            value={jump}
            onChange={(e) => setJump(e.target.value.replace(/\D/g, ''))}
            onPressEnter={() => { if (jump) { onGo(Number(jump)); setJump(''); } }}
            style={{ width: 58, height: 30, fontFamily: MONO }}
          />
        </>
      )}
    </div>
  );
}

export function PageBtn({ children, onClick, active, disabled, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      disabled={disabled}
      onClick={onClick}
      style={{
        height: 30, minWidth: 30, padding: '0 9px', borderRadius: 8,
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? PRIMARY : 'var(--surface2)',
        color: active ? '#fff' : disabled ? 'var(--dim)' : 'var(--text)',
        font: `${active ? 700 : 600} 12px ${MONO}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function LoadingRow() {
  return (
    <div style={{ padding: '34px 18px', display: 'flex', justifyContent: 'center' }}>
      <Spin />
    </div>
  );
}

/* ---- Shared style objects ---------------------------------------------- */

export const miniLabel = {
  font: '600 10.5px Outfit, sans-serif', color: 'var(--dim)',
  letterSpacing: '.05em', textTransform: 'uppercase',
};
export const ghostBtn = {
  height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 13px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
export const primaryBtn = {
  height: 38, padding: '0 18px', borderRadius: 9, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 13px Outfit, sans-serif', cursor: 'pointer',
};
export const filterBtn = {
  height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
};
export const rowBtn = {
  height: 30, padding: '0 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center',
};
/* Drawer actions sit a step below the page header's buttons, as the Ready Stock
   sheets' do — the drawer is already the focused surface. */
export const sheetCancelBtn = {
  height: 34, padding: '0 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--muted)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
export const sheetOkBtn = {
  height: 34, padding: '0 16px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 12.5px Outfit, sans-serif', cursor: 'pointer',
};
